/**
 * A selectable category facet for the Business Finder (FBOs). `value` is the full tag name the API returns on
 * FboResult.tags (so client-side filtering matches against it); `label` is the UI text. Fuel-type facets are
 * the exception: their `value` matches against FboResult.fuelTypes (from the BusinessFuel table), not tags.
 */
export interface FboFacetOption {
  label: string;
  value: string;
}

/**
 * Amenities facets (Amenities::*) — AND logic.
 */
export const FBO_AMENITIES_OPTIONS: FboFacetOption[] = [
  { label: 'Conference Room', value: 'Amenities::Conference Room' },
  { label: 'Fitness Center', value: 'Amenities::Fitness Center' },
  { label: 'Lounge', value: 'Amenities::Lounge' },
  { label: 'Office Space', value: 'Amenities::Office Space' },
  { label: 'Pet Friendly', value: 'Amenities::Pet Friendly' },
  { label: 'Pilot Supplies', value: 'Amenities::Pilot Supplies' },
  { label: 'Refreshments & Snack Vending', value: 'Amenities::Refreshments & Snack Vending' },
  { label: 'Rest Rooms', value: 'Amenities::Rest Rooms' },
  { label: 'Shower', value: 'Amenities::Shower' },
  { label: 'Snooze Room', value: 'Amenities::Snooze Room' },
  { label: 'Weather Briefing / Flight Planning Kiosk', value: 'Amenities::Weather Briefing Flight Planning Kiosk' }
];

/**
 * Facilities facets (Facilities::*) — AND logic.
 */
export const FBO_FACILITIES_OPTIONS: FboFacetOption[] = [
  { label: 'Hangar', value: 'Facilities::Hangar' },
  { label: 'Overnight', value: 'Facilities::Overnight' },
  { label: 'Seaplane - Beach', value: 'Facilities::Seaplane-Beach' },
  { label: 'Seaplane - Dock', value: 'Facilities::Seaplane-Dock' },
  { label: 'Seaplane - Ramp', value: 'Facilities::Seaplane-Ramp' },
  { label: 'Tie Down', value: 'Facilities::Tie Down' }
];

/**
 * Fuel facets (Fuel::*) — OR logic. Self-serve options first, then fuel providers.
 */
export const FBO_FUEL_OPTIONS: FboFacetOption[] = [
  { label: 'Self Serve', value: 'Fuel::Self Serve' },
  { label: 'Self Serve - 24 Hours', value: 'Fuel::Self Serve::24 Hours' },
  { label: 'Self Serve - After Hours', value: 'Fuel::Self Serve::After Hours' },
  { label: 'AEG Fuels', value: 'Fuel::Providers::AEG Fuels' },
  { label: 'Arrow Energy', value: 'Fuel::Providers::Arrow Energy' },
  { label: 'Ascent', value: 'Fuel::Providers::Ascent' },
  { label: 'AvCard', value: 'Fuel::Providers::AvCard' },
  { label: 'AvFuel', value: 'Fuel::Providers::AvFuel' },
  { label: 'Colt International', value: 'Fuel::Providers::Colt International' },
  { label: 'Epic', value: 'Fuel::Providers::Epic' },
  { label: 'Everest Fuel', value: 'Fuel::Providers::Everest Fuel' },
  { label: 'Mariah Fuels', value: 'Fuel::Providers::Mariah Fuels' },
  { label: 'Mercury Air Group', value: 'Fuel::Providers::Mercury Air Group' },
  { label: 'Multi-Service', value: 'Fuel::Providers::Multi-Service' },
  { label: 'Phillips Partners-Into-Planes', value: 'Fuel::Providers::Phillips Partners-Into-Planes' },
  { label: 'Titan Aviation', value: 'Fuel::Providers::Titan Aviation' },
  { label: 'Universal Fuels, Inc', value: 'Fuel::Providers::Universal Fuels, Inc' },
  { label: 'UVair', value: 'Fuel::Providers::UVair' },
  { label: 'Western Petroleum', value: 'Fuel::Providers::Western Petroleum' },
  { label: 'World Fuel', value: 'Fuel::Providers::World Fuel' },
  { label: 'Other', value: 'Fuel::Providers::Other' }
];

/**
 * Fuel Type facets — OR logic. Unlike the others, these match against FboResult.fuelTypes (the FuelType
 * values from the BusinessFuel table), not the tag list.
 */
export const FBO_FUEL_TYPE_OPTIONS: FboFacetOption[] = [
  { label: '100LL', value: '100LL' },
  { label: 'Jet A', value: 'Jet-A' },
  { label: 'Jet A (Premix/Prist)', value: 'Jet-A-Premix/Prist' },
  { label: 'MOGAS', value: 'MOGAS' },
  { label: 'UL94', value: 'UL94' }
];

/**
 * Line Services facets (Line Services::*) — AND logic.
 */
export const FBO_LINE_SERVICES_OPTIONS: FboFacetOption[] = [
  { label: '24 Hour Security', value: 'Line Services::24 Hour Security' },
  { label: 'Air Start Cart', value: 'Line Services::Air Start Cart' },
  { label: 'Airstairs', value: 'Line Services::Airstairs' },
  { label: 'Bahamas Gateway FBO', value: 'Line Services::Bahamas Gateway FBO' },
  { label: 'CAA Preferred FBO', value: 'Line Services::CAA Preferred FBO' },
  { label: 'Cargo', value: 'Line Services::Cargo' },
  { label: 'Catering', value: 'Line Services::Catering' },
  { label: 'Cleaning & Disinfection Service', value: 'Line Services::Cleaning & Disinfection Service' },
  { label: 'Concierge', value: 'Line Services::Concierge' },
  { label: 'DCA Gateway FBO', value: 'Line Services::DCA Gateway FBO' },
  { label: 'Deicing Fluid', value: 'Line Services::Deicing Fluid' },
  { label: 'Deicing I', value: 'Line Services::Deicing I' },
  { label: 'Deicing II', value: 'Line Services::Deicing II' },
  { label: 'Deicing IV', value: 'Line Services::Deicing IV' },
  { label: 'Deicing TKS', value: 'Line Services::Deicing TKS' },
  { label: 'GPU', value: 'Line Services::GPU' },
  { label: 'Ground Deicing', value: 'Line Services::Ground Deicing' },
  { label: 'Ground Handling', value: 'Line Services::Ground Handling' },
  { label: 'Hangar', value: 'Line Services::Hangar' },
  { label: 'Heated Hangar', value: 'Line Services::Heated Hangar' },
  { label: 'International Trash Disposal', value: 'Line Services::International Trash Disposal' },
  { label: 'Lavatory Service', value: 'Line Services::Lavatory Service' },
  { label: 'NATA Safety 1 FBO', value: 'Line Services::NATA Safety 1 FBO' },
  { label: 'NATA Safety 1st Clean', value: 'Line Services::NATA Safety 1st Clean' },
  { label: 'NBAA Member', value: 'Line Services::NBAA Member' },
  { label: 'Nitrogen', value: 'Line Services::Nitrogen' },
  { label: 'Oxygen Bottled', value: 'Line Services::Oxygen Bottled' },
  { label: 'Oxygen Bulk', value: 'Line Services::Oxygen Bulk' },
  { label: 'Potable Water', value: 'Line Services::Potable Water' },
  { label: 'Preheat', value: 'Line Services::Preheat' },
  { label: 'Quick-turn Service', value: 'Line Services::Quick-turn Service' },
  { label: 'Ramp Side Vehicle Access', value: 'Line Services::Ramp Side Vehicle Access' },
  { label: 'Tiedowns', value: 'Line Services::Tiedowns' },
  { label: 'Towing/Pushback', value: 'Line Services::Towing/Pushback' },
  { label: 'Trash Disposal', value: 'Line Services::Trash Disposal' },
  { label: 'Tug', value: 'Line Services::Tug' },
  { label: 'US Customs', value: 'Line Services::US Customs' },
  { label: 'US Government Contract Fuel', value: 'Line Services::US Government Contract Fuel' }
];

/**
 * Rental Car facets (Rental Car::*) — OR logic.
 */
export const FBO_RENTAL_CAR_OPTIONS: FboFacetOption[] = [
  { label: 'Avis', value: 'Rental Car::Avis' },
  { label: 'Budget', value: 'Rental Car::Budget' },
  { label: 'Dollar', value: 'Rental Car::Dollar' },
  { label: 'Enterprise', value: 'Rental Car::Enterprise' },
  { label: 'Go Rentals', value: 'Rental Car::Go Rentals' },
  { label: 'Hertz', value: 'Rental Car::Hertz' },
  { label: 'National', value: 'Rental Car::National' },
  { label: 'Sixt Car Rental', value: 'Rental Car::Sixt Car Rental' },
  { label: 'Thrifty', value: 'Rental Car::Thrifty' }
];

/**
 * Transportation facets (Transportation::*) — OR logic.
 */
export const FBO_TRANSPORTATION_OPTIONS: FboFacetOption[] = [
  { label: 'Bicycles', value: 'Transportation::Bicycles' },
  { label: 'Courtesy Transportation', value: 'Transportation::Courtesy Transportation' },
  { label: 'Golf Carts', value: 'Transportation::Golf Carts' },
  { label: 'Limousine', value: 'Transportation::Limousine' },
  { label: 'Public Transportation', value: 'Transportation::Public Transportation' },
  { label: 'Ride Share', value: 'Transportation::Ride Share' },
  { label: 'Shuttle Service', value: 'Transportation::Shuttle Service' },
  { label: 'Taxis', value: 'Transportation::Taxis' },
  { label: 'Van', value: 'Transportation::Van' }
];

/**
 * Filter state for the Business Finder (FBOs). Holds the keyword plus the selected category facets. Selected
 * facets are stored as arrays of tag names (or fuel-type values) so the OR/AND filtering logic (see
 * SearchPageComponent.filterFbos) can match directly against FboResult.tags / FboResult.fuelTypes. Empty
 * arrays mean "no constraint" (show all). Mirrors {@link FlightSchoolFinderFilter}.
 */
export class FboFinderFilter {

  /** Keyword: FBO name or airport ident. */
  name = '';

  /** Selected Amenities tag names (AND logic). */
  amenities: string[] = [];

  /** Selected Facilities tag names (AND logic). */
  facilities: string[] = [];

  /** Selected Fuel tag names (OR logic). */
  fuel: string[] = [];

  /** Selected Fuel Type values (OR logic; matched against FboResult.fuelTypes). */
  fuelType: string[] = [];

  /** Selected Line Services tag names (AND logic). */
  lineServices: string[] = [];

  /** Selected Rental Car tag names (OR logic). */
  rentalCar: string[] = [];

  /** Selected Transportation tag names (OR logic). */
  transportation: string[] = [];
}
